/**
 * Global Number and Currency Formatting Utilities
 *
 * All numbers in the application MUST use Western Arabic numerals (0-9)
 * with thousands separators (,) for consistency.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
 */

import type { Language } from "@/types/i18n";
import { RIYAL } from "@/lib/currency";

/**
 * Formats a number with thousands separators using Western Arabic numerals.
 * Works for all languages (Arabic and English).
 * @param value - The number to format.
 * @returns The formatted number string with commas.
 */
export function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return '0';
  }
  // Using 'en-US' locale ensures Western Arabic numerals (0-9) and comma separators.
  // Cap at 2 fraction digits: these are currency/quantity figures, so a raw
  // division artifact like 55221.7586842105 must render as "55,221.76", never
  // a 13-decimal string. Integers stay integer (minimumFractionDigits: 0).
  return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** Halalas → SAR with exactly two decimals (2071740 → "20,717.40"), as quotations print them. */
export function formatHalalas(minor: number): string {
  return (minor / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Formats a number as currency with thousands separators, sign first in
 * reading order (right of the number in Arabic, left in English).
 * @param value - The number to format.
 * @param currencySymbol - Currency symbol (default: the riyal sign, U+20C1).
 * @returns The formatted currency string (e.g., "⃁ 1,500,000").
 */
export function formatCurrency(
  value: number | string | null | undefined,
  currencySymbol: string = RIYAL
): string {
  const formattedNumber = formatNumber(value);
  // No-break space: the sign never wraps away from its number.
  return `${currencySymbol}\u00A0${formattedNumber}`;
}

/**
 * Formats a number as currency without the symbol.
 * @param value - The number to format.
 * @returns The formatted number string.
 */
export function formatCurrencyValue(value: number | string | null | undefined): string {
  return formatNumber(value);
}

/**
 * Formats a number as a percentage.
 * @param value - The number to format (e.g., 75 for 75% or 0.75 for 75%).
 * @param isDecimal - If true, treats value as decimal (0.75 = 75%).
 * @returns The formatted percentage string (e.g., "75%").
 */
export function formatPercentage(
  value: number | string | null | undefined,
  isDecimal: boolean = false
): string {
  if (value === null || value === undefined || value === '') {
    return '0%';
  }
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return '0%';
  }
  // If value is a decimal (e.g., 0.75), multiply by 100
  const percentage = isDecimal ? num * 100 : num;
  return `${formatNumber(Math.round(percentage))}%`;
}

/**
 * Formats area in square meters.
 * @param value - The area value.
 * @returns The formatted area string (e.g., "1,500 م²").
 */
export function formatArea(value: number | string | null | undefined): string {
  const formattedNumber = formatNumber(value);
  return `${formattedNumber} م²`;
}

/**
 * Formats area with English unit.
 * @param value - The area value.
 * @returns The formatted area string (e.g., "1,500 sqm").
 */
export function formatAreaEn(value: number | string | null | undefined): string {
  const formattedNumber = formatNumber(value);
  return `${formattedNumber} sqm`;
}

/**
 * Formats a decimal number with specified precision.
 * @param value - The number to format.
 * @param decimals - Number of decimal places.
 * @returns The formatted number string.
 */
export function formatDecimal(
  value: number | string | null | undefined,
  decimals: number = 2
): string {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return '0';
  }
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Chart axis tick formatter for large numbers.
 * @param value - The number to format.
 * @returns Abbreviated string (e.g., "1.5k", "2M").
 */
export function formatChartTick(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}k`;
  }
  return formatNumber(value);
}

// ─── Date Formatting ──────────────────────────────────────────────────────────
//
// Core: formatDate() — takes Intl.DateTimeFormatOptions, returns locale-aware string.
// All other date functions call formatDate() with specific options.
// Always outputs Western Arabic numerals (0-9) via ar-u-nu-latn locale.
//
// formatDayMonth()       — "15 Mar"           / "15 مارس"
// formatDayMonthYear()   — "15 Mar 2026"      / "15 مارس 2026"
// formatDayMonthLong()   — "15 March"         / "15 مارس"
// formatDayMonthLongYear() — "15 March 2026"  / "15 مارس 2026"

type DateInput = string | Date | null | undefined;

/**
 * Core date formatter. Parses the input and delegates to toLocaleDateString().
 * Always uses Western numerals (0-9) regardless of language.
 *
 * @param dateStr - Date string, Date object, null, or undefined.
 * @param language - "ar" or "en" (defaults to "en").
 * @param options - Intl.DateTimeFormatOptions passed to toLocaleDateString().
 * @returns Formatted date string, or "-" for invalid/missing input.
 */
export function formatDate(
  dateStr: DateInput,
  language: Language = "en",
  options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" }
): string {
  if (!dateStr) return '-';
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  if (isNaN(date.getTime())) return '-';
  const locale = language === "ar" ? "ar-u-nu-latn" : "en-US";
  return date.toLocaleDateString(locale, options);
}

/** "15 Mar" / "15 مارس" */
export function formatDayMonth(dateStr: DateInput, language: Language = "en"): string {
  return formatDate(dateStr, language, { day: "numeric", month: "short" });
}

/** "15 Mar 2026" / "15 مارس 2026" */
export function formatDayMonthYear(dateStr: DateInput, language: Language = "en"): string {
  return formatDate(dateStr, language, { day: "numeric", month: "short", year: "numeric" });
}

/** "15 March" / "15 مارس" */
export function formatDayMonthLong(dateStr: DateInput, language: Language = "en"): string {
  return formatDate(dateStr, language, { day: "numeric", month: "long" });
}

/** "15 March 2026" / "15 مارس 2026" */
export function formatDayMonthLongYear(dateStr: DateInput, language: Language = "en"): string {
  return formatDate(dateStr, language, { day: "numeric", month: "long", year: "numeric" });
}

